import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class HelpModuleService {
    private categorySelectedInFaq = new BehaviorSubject(null);
    private categorySelectedInUserManuals = new BehaviorSubject<void>(null);
    private categoryUpdateSubject = new BehaviorSubject<void>(null);
    selectedCategoryInFaq$ = this.categorySelectedInFaq.asObservable();
    selectedCategoryInUserManuals$ = this.categorySelectedInUserManuals.asObservable();
    categoryUpdate$ = this.categoryUpdateSubject.asObservable();

    // Local data cache for optimization
    private allFaqData: any[] = [];
    private allDocumentsData: any[] = [];
    private filteredFaqData = new BehaviorSubject<any[]>([]);
    private filteredDocumentsData = new BehaviorSubject<any[]>([]);
    filteredFaqData$ = this.filteredFaqData.asObservable();
    filteredDocumentsData$ = this.filteredDocumentsData.asObservable();

    constructor(private http: HttpClient) {}

    setSelectedCategoryInFaq(category: any) {
        this.categorySelectedInFaq.next(category);
    }

    setSelectedCategoryInUserManuals(category: any) {
        this.categorySelectedInUserManuals.next(category);
    }

    updateCategories() {
        this.categoryUpdateSubject.next();
    }

    getFaq(parentCategoryId: number = 0, helpCategoryId: number = 0, isForAdministration = true) {
        return this.http.get<any>(
            `api:///HelpModule/GetFaq/${parentCategoryId}/${helpCategoryId}/${isForAdministration}`
        );
    }

    getFaqAttachments(faqId: number) {
        return this.http.get<any>(`api:///HelpModule/getFaqAttachments/${faqId}`);
    }

    getFaqAttachment(attachmentId: number) {
        return this.http.get<any>(`api:///HelpModule/getFaqAttachment/${attachmentId}`);
    }

    addQuestion(body: any): Observable<any> {
        return this.http.post<any>('api:///HelpModule/AddQuestion', body);
    }

    removeQuestion(ids: any): Observable<any> {
        return this.http.post<any>('api:///HelpModule/RemoveQuestion/', ids);
    }

    archiveQuestion(ids: any) {
        return this.http.post<any>('api:///HelpModule/ArchiveQuestion/', ids);
    }

    getDocuments(parentCategoryId: number = 0, helpCategoryId: number = 0, isForAdministration = true) {
        return this.http.get<any>(
            `api:///HelpModule/GetDocuments/${parentCategoryId}/${helpCategoryId}/${isForAdministration}`
        );
    }

    getDocumentAttachments(documentId: number) {
        return this.http.get<any>(`api:///HelpModule/getDocumentAttachments/${documentId}`);
    }

    getDocumentAttachment(attachmentId: number) {
        return this.http.get<any>(`api:///HelpModule/getDocumentAttachment/${attachmentId}`);
    }

    addDocument(body: any): Observable<any> {
        return this.http.post<any>('api:///HelpModule/AddDocument', body);
    }

    removeDocument(ids: any): Observable<any> {
        return this.http.post<any>('api:///HelpModule/RemoveDocument/', ids);
    }

    archiveDocument(ids: any) {
        return this.http.post<any>('api:///HelpModule/ArchiveDocument/', ids);
    }

    // Methods for local data caching and filtering
    setAllFaqData(data: any[]) {
        this.allFaqData = data;
    }

    setAllDocumentsData(data: any[]) {
        this.allDocumentsData = data;
    }

    setFilteredFaqData(data: any[]) {
        this.filteredFaqData.next(data);
    }

    setFilteredDocumentsData(data: any[]) {
        this.filteredDocumentsData.next(data);
    }

    getAllFaqData(): any[] {
        return this.allFaqData;
    }

    getAllDocumentsData(): any[] {
        return this.allDocumentsData;
    }

    // Method to filter FAQ data for a specific category locally
    getFilteredFaqForCategory(category: any): any[] {
        if (!category) return [];

        const parentCategoryId = category.parentCategoryId ? category.parentCategoryId : category.id;
        const childCategoryId = category.parentCategoryId ? category.id : 0;

        return this.allFaqData.filter((faq) => {
            if (childCategoryId === 0) {
                // Parent category selected - show all FAQ in this category and its subcategories
                return faq.parentCategoryId === parentCategoryId && !faq.archived;
            } else {
                // Subcategory selected - show only FAQ in this specific subcategory
                return (
                    faq.helpCategoryId === childCategoryId && faq.parentCategoryId === parentCategoryId && !faq.archived
                );
            }
        });
    }

    // Method to filter documents data for a specific category locally
    getFilteredDocumentsForCategory(category: any): any[] {
        if (!category) return [];

        const parentCategoryId = category.parentCategoryId ? category.parentCategoryId : category.id;
        const childCategoryId = category.parentCategoryId ? category.id : 0;

        return this.allDocumentsData.filter((document) => {
            if (childCategoryId === 0) {
                // Parent category selected - show all documents in this category and its subcategories
                return document.parentCategoryId === parentCategoryId && !document.archived;
            } else {
                // Subcategory selected - show only documents in this specific subcategory
                return (
                    document.helpCategoryId === childCategoryId &&
                    document.parentCategoryId === parentCategoryId &&
                    !document.archived
                );
            }
        });
    }
}
